import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetPricingPlanTickRate } from 'src/features/pricingPlan/api/tickRate/useGetPricingPlanTickRate';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import TickRateForm from 'src/features/pricingPlan/components/form/tickRate/TickRateForm';
import {
    PricingPlanTickRateCreateValues,
    pricingPlanTickRateFormSchema,
    PricingPlanTickRateFormSchemaValues,
} from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages.ts';
import { PRICING_PLAN_STATUS, PricingPlanTickRate } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface TickRateFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTickRate?: PricingPlanTickRate;
    children?: React.ReactNode;
    showToast: React.RefObject<boolean>;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PricingPlanTickRateCreateValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function TickRateFormModal<TData>({
    selectedTickRate,
    className,
    showToast,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: TickRateFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();
    const { planId = '' } = useParams();

    const { data: pricingPlanData } = useGetPricingPlan({
        parkingId,
        planId,
        config: { enabled: !!planId },
    });

    const { data: tickRate } = useGetPricingPlanTickRate({
        planId: selectedTickRate?.pricing_plan ?? '',
        parkingId,
        tickRateId: selectedTickRate?.id ?? '',
        config: { enabled: !!selectedTickRate?.pricing_plan && !!selectedTickRate?.id && isModalOpen },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            start_day: tickRate?.start_day ?? undefined,
            end_day: tickRate?.end_day ?? undefined,
            from_time: tickRate?.from_time ?? undefined,
            to_time: tickRate?.to_time ?? undefined,
            rate_with_tax: tickRate?.rate_with_tax ?? undefined,
            max_amount: tickRate?.max_amount ?? null,
        };
    }, [tickRate]);

    const form = useForm<PricingPlanTickRateFormSchemaValues>({
        resolver: zodResolver(pricingPlanTickRateFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [tickRate, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PricingPlanTickRateFormSchemaValues) => {
            if (showToast && 'current' in showToast) {
                (showToast as React.RefObject<boolean>).current = false;
            }

            // Pin the seconds to 0: the bands are compared by the minute, and "to" is the minute the band ends at
            // (not in it), so the next band can start at that very minute.
            const setTimeSeconds = (time: string, seconds: number) => {
                const [hours = '00', minutes = '00'] = time.split(':');
                return `${hours}:${minutes}:${String(seconds).padStart(2, '0')}`;
            };

            mutate(
                {
                    ...values,
                    from_time: setTimeSeconds(values.from_time, 0),
                    to_time: setTimeSeconds(values.to_time, 0),
                },
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, showToast, form, setIsModalOpen],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-block">
                                <DialogTrigger asChild>
                                    <Button
                                        className={className}
                                        disabled={pricingPlanData?.status === PRICING_PLAN_STATUS.PUBLISHED}
                                    >
                                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                                        {title}
                                    </Button>
                                </DialogTrigger>
                            </span>
                        </TooltipTrigger>
                        {pricingPlanData?.status === PRICING_PLAN_STATUS.PUBLISHED && (
                            <TooltipContent side="top" className="max-w-56">
                                <FormattedMessage {...pricingPlanMessages.publishedPricingPlanError} />
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <TickRateForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default TickRateFormModal;
